import * as Tone from 'tone';
import { state } from './state.js';
import { playSound, thudBounceSynth } from './audio.js';
import { triggerAchievement } from './ui.js';
import {
    GRAVITY_CONSTANT,
    GAME_SPEED_MULTIPLIER,
    PLAY_AREA_WIDTH,
    PLAY_AREA_HEIGHT,
    COLLISION_DAMPING,
    MIN_SPEED_TO_HALVE_AFTER_COLLISION,
    MIN_SPEED_TO_STOP_AFTER_COLLISION,
    MIN_SPEED_TO_STOP_IN_SPACE,
    MIN_GRAVITY_MAGNITUDE_TO_KEEP_MOVING,
    BALL_RADIUS,
    BUNKER_ESCAPE_GRACE_DURATION,
    BUNKER_STICK_DEPTH_FRAC,
    BUNKER_FLOOR_DEPTH_FRAC,
    BUNKER_LATERAL_CHECK_GRACE_FRAC,
    LAUNCH_ESCAPE_GRACE_DURATION
} from './constants.js';

// Clean registration of callbacks to prevent circular dependencies
let arcadeGameOverCallback = null;
export function registerArcadeGameOverCallback(callback) {
    arcadeGameOverCallback = callback;
}

// --- Math Utilities ---
export function lerp(a, b, t) { return a * (1 - t) + b * t; }
export function clamp(value, min, max) { return Math.min(Math.max(value, min), max); }
export function distSq(x1, y1, x2, y2) { 
    const dx = x1 - x2; 
    const dy = y1 - y2; 
    return dx * dx + dy * dy; 
}

// --- Gravity Calculations ---
export function calculateGravityAcceleration(x, y, planets) {
    let ax_total = 0, ay_total = 0;
    planets.forEach(pl => {
        const dx = pl.x - x, dy = pl.y - y;
        let d2 = dx*dx + dy*dy;
        const min_d2 = (pl.r * 0.5) * (pl.r * 0.5); 
        if (d2 < min_d2) d2 = min_d2; 
        if (d2 > 1e-4) { 
            const d = Math.sqrt(d2);
            const accelerationMagnitude = (pl.mass / d2) * GRAVITY_CONSTANT; 
            ax_total += accelerationMagnitude * (dx / d);
            ay_total += accelerationMagnitude * (dy / d);
        }
    });
    return { ax: ax_total, ay: ay_total };
}

// --- Physics Engine Events ---
export function handleSingleBallStopped(ball) {
    if (!ball) return;
    ball.vx = 0; ball.vy = 0; ball.moving = false;
    ball.isEscapingBunkerPlanetIdx = -1;
    ball.escapingBunkerGraceTime = 0;
    ball.isEscapingPlanetIdx = -1;
    ball.escapingPlanetGraceTime = 0;
    ball.homingActiveThisShot = false;
    ball.isStickyShot = false;


    state.gameState.lastShotDistanceTraveled = ball.currentShotDistanceTraveled;
    state.gameState.previousShotTrail = [...ball.trail];

    // Determine if the ball stopped inside a bunker
    let stoppedInBunker = false;
    if (state.gameState.planets) {
        for (let pi = 0; pi < state.gameState.planets.length; pi++) {
            const p = state.gameState.planets[pi];
            if (p.bunkers && p.bunkers.length > 0) {
                for (const bunker of p.bunkers) {
                    const bunkerWorldSurfaceX = p.x + p.r * Math.cos(bunker.angle);
                    const bunkerWorldSurfaceY = p.y + p.r * Math.sin(bunker.angle);
                    const effectiveBunkerRadiusForLateralCheck = bunker.radius + ball.r * BUNKER_LATERAL_CHECK_GRACE_FRAC;
                    if (distSq(ball.x, ball.y, bunkerWorldSurfaceX, bunkerWorldSurfaceY) < (effectiveBunkerRadiusForLateralCheck * effectiveBunkerRadiusForLateralCheck)) {
                        stoppedInBunker = true;
                        break;
                    }
                }
            }
            if (stoppedInBunker) break;
        }
    }
    ball.isInBunker = stoppedInBunker;

    if (stoppedInBunker && ball.id === 0) {
        state.bunkerHitsInCurrentHole = (state.bunkerHitsInCurrentHole || 0) + 1;
        if (state.bunkerHitsInCurrentHole >= 3) {
            triggerAchievement('muck');
        }
    }

    if (ball.id === 0 && ball.currentShotDistanceTraveled > 4500) {
        triggerAchievement('slingshot');
    }

    // Determine which planet index the ball stopped on (if any)
    let stoppedOnPlanetIdx = -1;
    if (state.gameState.planets) {
        for (let pi = 0; pi < state.gameState.planets.length; pi++) {
            const p = state.gameState.planets[pi];
            const dist = Math.hypot(ball.x - p.x, ball.y - p.y);
            if (dist < p.r + ball.r + 2.5) {
                stoppedOnPlanetIdx = pi;
                break;
            }
        }
    }

    // Galactic Pachinko: bounce off 2 or more OTHER planets, and land back on the launch planet!
    if (ball.id === 0 && ball.launchPlanetIdx !== -1 && stoppedOnPlanetIdx === ball.launchPlanetIdx && ball.bouncedPlanetIndices) {
        const uniqueBounces = ball.bouncedPlanetIndices.filter(idx => idx !== ball.launchPlanetIdx);
        if (uniqueBounces.length >= 2) {
            triggerAchievement('pachinko');
        }
    }

    if (state.currentPlayingMode === 'powerup' && !state.gameState.goalReached && !state.isArcadeRunOver) {
        // Only trigger game over if it's NOT a multi-ball situation currently.
        // If it IS multi-ball, the game over check will happen after all multi-balls resolve.
        if (!state.gameState.multiBallActiveOnScreen && state.shotCount >= state.currentPar) {
            console.log(`Arcade Game Over (ball stopped over par, not multi-ball phase): Hole ${state.totalScore + 1}, Shots ${state.shotCount} >= Par ${state.currentPar}.`);
            if (arcadeGameOverCallback) {
                arcadeGameOverCallback();
            }
            return; 
        } else if (state.gameState.multiBallActiveOnScreen) {
            console.log(`Ball ${ball.id} stopped during active multi-ball. Shot ${state.shotCount}, Par ${state.currentPar}. Deferring game over check.`);
        }
    }
}

export function resolveCollision(ball, planet, collisionRadius = planet.r) {
    if (state.gameState.planets) {
        const planetIdx = state.gameState.planets.indexOf(planet);
        if (planetIdx !== -1 && ball.bouncedPlanetIndices) {
            if (!ball.bouncedPlanetIndices.includes(planetIdx)) {
                ball.bouncedPlanetIndices.push(planetIdx);
            }
        }
    }
    ball.potentialGrazePlanetIdx = -1; // Collided with solid planet, cancel grazing state
    if (ball.id === 0) {
        state.hasBouncedOffRockInCurrentHole = true;
    }
    const dx = ball.x - planet.x, dy = ball.y - planet.y;
    const distance = Math.hypot(dx, dy);
    if (distance === 0) return; // Prevent division by zero
    const nx = dx / distance, ny = dy / distance;
    const overlap = (ball.r + collisionRadius) - distance;

    ball.x += nx * overlap * 1.01; 
    ball.y += ny * overlap * 1.01;

    // Apply sideways/tangential sliding friction (prevents endless sliding/jiggling on surface)
    const tx = -ny;
    const ty = nx;
    const velocityAlongNormal = ball.vx * nx + ball.vy * ny;
    const velocityAlongTangent = ball.vx * tx + ball.vy * ty;
    
    const slideFriction = 0.94; // smooth tangential damping per frame
    const newTangentVelocity = velocityAlongTangent * slideFriction;
    
    // Reconstruct velocity
    ball.vx = velocityAlongNormal * nx + newTangentVelocity * tx;
    ball.vy = velocityAlongNormal * ny + newTangentVelocity * ty;

    if (velocityAlongNormal > 0) return; 

    // Impart impact visual closed eyes state (satisfying 1.3s duration!)
    ball.impactTimer = 1.3;

    // Impart physical visual spin based on relative sliding impact angle!
    if (ball.angularVelocity === undefined) ball.angularVelocity = 0;
    ball.angularVelocity += (velocityAlongTangent * 0.12);
    ball.angularVelocity = Math.max(-15, Math.min(15, ball.angularVelocity));

    if (ball.linearLaserActive) {
        ball.linearLaserActive = false;
        console.log(`Ball ${ball.id} Linear Laser deactivated on surface collision with planet.`);
    }

    if (ball.isStickyShot) { 
        ball.vx = 0;
        ball.vy = 0;
        state.gameState.lastShotDistanceTraveled = ball.currentShotDistanceTraveled;
        console.log(`Ball ${ball.id} stopped: Sticky Ball effect on collision with planet ${state.gameState.planets.indexOf(planet)}.`);
        playSound(thudBounceSynth, "A2", "16n", Tone.now(), 0.8); 
        handleSingleBallStopped(ball); 
        return; 
    }

    const restitution = 0.9; 
    const impulseScalar = -(1 + restitution) * velocityAlongNormal;
    ball.vx += impulseScalar * nx;
    ball.vy += impulseScalar * ny;

    ball.vx *= COLLISION_DAMPING;
    ball.vy *= COLLISION_DAMPING;

    playSound(thudBounceSynth, "C4", "32n", Tone.now(), Math.min(1, Math.abs(velocityAlongNormal) / 30));

    const currentSpeed = Math.hypot(ball.vx, ball.vy);
    if (currentSpeed < MIN_SPEED_TO_HALVE_AFTER_COLLISION * GAME_SPEED_MULTIPLIER) { 
        ball.vx *= 0.5; 
        ball.vy *= 0.5;
        if (currentSpeed < MIN_SPEED_TO_STOP_AFTER_COLLISION * GAME_SPEED_MULTIPLIER || Math.hypot(ball.vx,ball.vy) < MIN_SPEED_TO_STOP_AFTER_COLLISION * GAME_SPEED_MULTIPLIER) { 
            state.gameState.lastShotDistanceTraveled = ball.currentShotDistanceTraveled; 
            console.log(`Ball ${ball.id} stopped: collision low speed after damping.`);
            handleSingleBallStopped(ball);
        }
    }
}

export function resolveBunkerCollision(ball, bwsX, bwsY, bunkerRadius, sandTrapSound) {
    ball.potentialGrazePlanetIdx = -1; // Collided with bunker, cancel grazing state
    const dx = ball.x - bwsX, dy = ball.y - bwsY;
    const distance = Math.hypot(dx, dy);
    if (distance === 0) return;
    const nx = dx / distance, ny = dy / distance;
    const overlap = distance - (bunkerRadius - ball.r);

    ball.x -= nx * overlap * 1.01; 
    ball.y -= ny * overlap * 1.01;

    const velocityAlongNormal = ball.vx * nx + ball.vy * ny;
    if (velocityAlongNormal < 0) return; // already moving away from sand wall

    ball.impactTimer = 1.3; // closed eyes on sand trap impact (satisfying 1.3s duration!)
    if (ball.angularVelocity !== undefined) {
        ball.angularVelocity *= 0.5; // Sand absorbs rotational energy!
    }

    if (ball.linearLaserActive) {
        ball.linearLaserActive = false;
        console.log(`Ball ${ball.id} Linear Laser deactivated on bunker collision.`);
    }

    if (ball.isStickyShot) { 
        ball.vx = 0;
        ball.vy = 0;
        state.gameState.lastShotDistanceTraveled = ball.currentShotDistanceTraveled;
        playSound(thudBounceSynth, "A2", "16n", Tone.now(), 0.8); 
        handleSingleBallStopped(ball); 
        return; 
    }

    const restitution = 0.4; // Soft sand absorption
    const impulseScalar = -(1 + restitution) * velocityAlongNormal;
    ball.vx += impulseScalar * nx;
    ball.vy += impulseScalar * ny;

    ball.vx *= COLLISION_DAMPING;
    ball.vy *= COLLISION_DAMPING;

    playSound(sandTrapSound, null, "0.15s", Tone.now());

    const currentSpeed = Math.hypot(ball.vx, ball.vy);
    if (currentSpeed < MIN_SPEED_TO_HALVE_AFTER_COLLISION * GAME_SPEED_MULTIPLIER) { 
        ball.vx *= 0.5; 
        ball.vy *= 0.5;
        if (currentSpeed < MIN_SPEED_TO_STOP_AFTER_COLLISION * GAME_SPEED_MULTIPLIER || Math.hypot(ball.vx,ball.vy) < MIN_SPEED_TO_STOP_AFTER_COLLISION * GAME_SPEED_MULTIPLIER) { 
            state.gameState.lastShotDistanceTraveled = ball.currentShotDistanceTraveled; 
            handleSingleBallStopped(ball);
        }
    }
}

export function checkGoal(ball, planet, goalData) {
    const holeCenterX = planet.x + planet.r * Math.cos(goalData.angleOnPlanet);
    const holeCenterY = planet.y + planet.r * Math.sin(goalData.angleOnPlanet);
    return Math.hypot(ball.x - holeCenterX, ball.y - holeCenterY) < goalData.holeRadius + ball.r * 0.25;
}

export function predictTrajectory(startX, startY, initialVx, initialVy, steps, deltaTimeStep, hostPlanetIndexForBunkerShot = -1, linearLaserActive = false, customPlanets = null) {
    let x = startX, y = startY, vx = initialVx, vy = initialVy;
    const trajectoryPoints = [];
    const primaryBallRadius = state.gameState.balls[0] ? state.gameState.balls[0].r : BALL_RADIUS;
    const planetsList = customPlanets || state.gameState.planets;
    
    // Identify launch planet index (resting on or extremely close to its surface)
    let launchPlanetIdx = -1;
    if (planetsList && planetsList.length > 0) {
        for (let pi = 0; pi < planetsList.length; pi++) {
            const p = planetsList[pi];
            const dist = Math.hypot(startX - p.x, startY - p.y);
            if (dist < p.r + primaryBallRadius + 2.0) {
                const dx = startX - p.x;
                const dy = startY - p.y;
                const dot = initialVx * dx + initialVy * dy;
                // Only bypass if the shot is outward or tangent (not inward into the planet)
                if (dot >= -0.01) {
                    launchPlanetIdx = pi;
                }
                break;
            }
        }
    }
    let remainingGraceTime = launchPlanetIdx !== -1 ? LAUNCH_ESCAPE_GRACE_DURATION : 0;
    
    let nextIsBounce = false;
    let nextBounceType = null;
    let laserActive = linearLaserActive;
    
    for (let i = 0; i < steps; i++) {
        trajectoryPoints.push({
            x, 
            y, 
            isBounce: nextIsBounce, 
            bounceType: nextBounceType
        });
        
        nextIsBounce = false;
        nextBounceType = null;
        
        const { ax, ay } = laserActive ? { ax: 0, ay: 0 } : calculateGravityAcceleration(x, y, planetsList);
        
        vx += ax * deltaTimeStep * GAME_SPEED_MULTIPLIER; 
        vy += ay * deltaTimeStep * GAME_SPEED_MULTIPLIER;
        x += vx * deltaTimeStep * GAME_SPEED_MULTIPLIER; 
        y += vy * deltaTimeStep * GAME_SPEED_MULTIPLIER;
        
        let shouldStopPrediction = false;
        
        if (planetsList && planetsList.length > 0) { 
            for (let planetIdx = 0; planetIdx < planetsList.length; planetIdx++) {
                const p = planetsList[planetIdx];
                if (!p || typeof p.r === 'undefined') continue;

                // 1. Lateral bunker check (always active, not bypassed by grace steps)
                let predBallIsLaterallyOverBunker = false;
                if (p.bunkers && p.bunkers.length > 0) {
                    for (const bunker of p.bunkers) {
                        const bunkerWorldSurfaceX = p.x + p.r * Math.cos(bunker.angle);
                        const bunkerWorldSurfaceY = p.y + p.r * Math.sin(bunker.angle);
                        const effectiveBunkerRadiusForPredCheck = bunker.radius + primaryBallRadius * BUNKER_LATERAL_CHECK_GRACE_FRAC;
                        const predRadialDist = Math.hypot(x - p.x, y - p.y);

                        if (predRadialDist < p.r && distSq(x, y, bunkerWorldSurfaceX, bunkerWorldSurfaceY) < (effectiveBunkerRadiusForPredCheck * effectiveBunkerRadiusForPredCheck)) {
                            predBallIsLaterallyOverBunker = true;
                            const dPred = Math.hypot(x - bunkerWorldSurfaceX, y - bunkerWorldSurfaceY);
                            
                            // If the ball hits the sand wall/floor (even near the lip), it instantly settles and stops.
                            const dx = x - bunkerWorldSurfaceX;
                            const dy = y - bunkerWorldSurfaceY;
                            const nx = dPred > 0 ? dx / dPred : 0;
                            const ny = dPred > 0 ? dy / dPred : 0;
                            const velocityAlongNormal = vx * nx + vy * ny;
                            
                            if (predRadialDist < p.r && dPred >= bunker.radius - primaryBallRadius && velocityAlongNormal > 0) {
                                shouldStopPrediction = true;
                                break; // Stop predicting! No bounces inside the sand trap!
                            }
                            break; 
                        }
                    }
                }
                
                if (shouldStopPrediction) break;

                // 2. Solid planet rock penetration check:
                let isInsideSolidRock = false;
                const distToPlanetCenter = Math.hypot(x - p.x, y - p.y);
                if (distToPlanetCenter < p.r - 0.5) {
                    let insideAnyCrater = false;
                    if (p.bunkers && p.bunkers.length > 0) {
                        for (const bunker of p.bunkers) {
                            const bunkerWorldSurfaceX = p.x + p.r * Math.cos(bunker.angle);
                            const bunkerWorldSurfaceY = p.y + p.r * Math.sin(bunker.angle);
                            const dBunkerCorrect = Math.hypot(x - bunkerWorldSurfaceX, y - bunkerWorldSurfaceY);
                            // Allow a buffer to let the sand sticking check resolve before solid rock triggers
                            if (dBunkerCorrect < bunker.radius + primaryBallRadius * 1.5) {
                                insideAnyCrater = true;
                                break;
                            }
                        }
                    }
                    if (!insideAnyCrater) {
                        isInsideSolidRock = true;
                    }
                }

                const isBypassedLaunchPlanet = (remainingGraceTime > 0 && planetIdx === launchPlanetIdx);

                if (isInsideSolidRock && !isBypassedLaunchPlanet) {
                    // Resolve bounce collision on solid rock inside planet!
                    const dx = x - p.x, dy = y - p.y;
                    const distance = Math.hypot(dx, dy);
                    if (distance > 0) {
                        const nx = dx / distance, ny = dy / distance;
                        const overlap = (primaryBallRadius + p.r) - distance;
                        x += nx * overlap * 1.01;
                        y += ny * overlap * 1.01;
                        
                        // Apply sideways/tangential sliding friction
                        const tx = -ny;
                        const ty = nx;
                        const velocityAlongNormal = vx * nx + vy * ny;
                        const velocityAlongTangent = vx * tx + vy * ty;
                        
                        const slideFriction = 0.94;
                        const newTangentVelocity = velocityAlongTangent * slideFriction;
                        
                        // Reconstruct velocity
                        vx = velocityAlongNormal * nx + newTangentVelocity * tx;
                        vy = velocityAlongNormal * ny + newTangentVelocity * ty;

                        if (velocityAlongNormal < 0) {
                            const restitution = 0.9;
                            const impulseScalar = -(1 + restitution) * velocityAlongNormal;
                            vx += impulseScalar * nx;
                            vy += impulseScalar * ny;
                            vx *= COLLISION_DAMPING;
                            vy *= COLLISION_DAMPING;
                            
                            const currentSpeed = Math.hypot(vx, vy);
                            if (currentSpeed < MIN_SPEED_TO_HALVE_AFTER_COLLISION * GAME_SPEED_MULTIPLIER) {
                                vx *= 0.5;
                                vy *= 0.5;
                                if (currentSpeed < MIN_SPEED_TO_STOP_AFTER_COLLISION * GAME_SPEED_MULTIPLIER || Math.hypot(vx, vy) < MIN_SPEED_TO_STOP_AFTER_COLLISION * GAME_SPEED_MULTIPLIER) {
                                    vx = 0;
                                    vy = 0;
                                    shouldStopPrediction = true;
                                }
                            }
                            
                            nextIsBounce = true;
                            nextBounceType = 'rock';
                            laserActive = false; // Laser deactivates on solid rock bounce!
                        }
                    }
                    break; // bounce calculated, break planet check for this step
                }

                // 3. Standard planet surface check (only bypassed when over bunker mouth)
                let isOverBunkerMouth = false;
                if (p.bunkers && p.bunkers.length > 0) {
                    for (const bunker of p.bunkers) {
                        const bunkerWorldSurfaceX = p.x + p.r * Math.cos(bunker.angle);
                        const bunkerWorldSurfaceY = p.y + p.r * Math.sin(bunker.angle);
                        const dBunker = Math.hypot(x - bunkerWorldSurfaceX, y - bunkerWorldSurfaceY);
                        if (dBunker < bunker.radius) {
                            isOverBunkerMouth = true;
                            break;
                        }
                    }
                }

                if (!predBallIsLaterallyOverBunker && !isOverBunkerMouth && !isBypassedLaunchPlanet) { 
                    const distToPlanetCenter = Math.hypot(x - p.x, y - p.y);
                    if (distToPlanetCenter < primaryBallRadius + p.r) {
                        // Resolve bounce collision on planet surface!
                        const dx = x - p.x, dy = y - p.y;
                        const distance = Math.hypot(dx, dy);
                        if (distance > 0) {
                            const nx = dx / distance, ny = dy / distance;
                            const overlap = (primaryBallRadius + p.r) - distance;
                            x += nx * overlap * 1.01;
                            y += ny * overlap * 1.01;
                            
                            // Apply sideways/tangential sliding friction
                            const tx = -ny;
                            const ty = nx;
                            const velocityAlongNormal = vx * nx + vy * ny;
                            const velocityAlongTangent = vx * tx + vy * ty;
                            
                            const slideFriction = 0.94;
                            const newTangentVelocity = velocityAlongTangent * slideFriction;
                            
                            // Reconstruct velocity
                            vx = velocityAlongNormal * nx + newTangentVelocity * tx;
                            vy = velocityAlongNormal * ny + newTangentVelocity * ty;

                            if (velocityAlongNormal < 0) {
                                const restitution = 0.9;
                                const impulseScalar = -(1 + restitution) * velocityAlongNormal;
                                vx += impulseScalar * nx;
                                vy += impulseScalar * ny;
                                vx *= COLLISION_DAMPING;
                                vy *= COLLISION_DAMPING;
                                
                                const currentSpeed = Math.hypot(vx, vy);
                                if (currentSpeed < MIN_SPEED_TO_HALVE_AFTER_COLLISION * GAME_SPEED_MULTIPLIER) {
                                    vx *= 0.5;
                                    vy *= 0.5;
                                    if (currentSpeed < MIN_SPEED_TO_STOP_AFTER_COLLISION * GAME_SPEED_MULTIPLIER || Math.hypot(vx, vy) < MIN_SPEED_TO_STOP_AFTER_COLLISION * GAME_SPEED_MULTIPLIER) {
                                        vx = 0;
                                        vy = 0;
                                        shouldStopPrediction = true;
                                    }
                                }
                                
                                nextIsBounce = true;
                                nextBounceType = 'surface';
                                laserActive = false; // Laser deactivates on planet surface bounce!
                            }
                        }
                        break; 
                    }
                }
            }
        }
        if (shouldStopPrediction) break;
        if (x < 0 || x > PLAY_AREA_WIDTH || y < 0 || y > PLAY_AREA_HEIGHT) break; 
        
        // Stop predicting if velocity gets extremely low (effectively stopped)
        if (Math.hypot(vx, vy) < MIN_SPEED_TO_STOP_IN_SPACE * GAME_SPEED_MULTIPLIER) {
            break;
        }
        if (remainingGraceTime > 0 && launchPlanetIdx !== -1) {
            const launchPlanet = planetsList[launchPlanetIdx];
            if (launchPlanet) {
                const dist = Math.hypot(x - launchPlanet.x, y - launchPlanet.y);
                if (dist >= launchPlanet.r + primaryBallRadius + 0.5) {
                    remainingGraceTime = 0;
                }
            }
        }
        remainingGraceTime = Math.max(0, remainingGraceTime - deltaTimeStep * GAME_SPEED_MULTIPLIER);
    }
    return trajectoryPoints;
}
